/**
 * What happens when a person picks an address suggestion — shared by the web
 * field (`@munifocus/ui`) and the mobile one, which cannot share a component
 * but must not disagree about the address that gets filed.
 */

/**
 * A civic number at the start of an address: `45`, `45A`, `12-14`, and the
 * French `45, rue Weldon` form.
 */
const CIVIC_NUMBER = /^\s*(\d+[A-Za-z]?(?:[-–]\d+[A-Za-z]?)?)(?=[\s,])/;

/**
 * Keep the civic number the person typed when the place they picked has none.
 *
 * OSM (and Google, for a street-level match) often knows the street but not the
 * house: typing "45 Elm Street, Moncton" offers "Elm Street, Moncton, NB", and
 * taking that verbatim filed the report against the whole street. The number is
 * the one part of the address only the person typing it knows, so it wins; the
 * place still supplies the spelling, the city and the coordinates. A place that
 * carries its own number is an exact civic match and is trusted as-is.
 */
export function withTypedCivicNumber<P extends { address: string }>(typed: string, place: P): P {
  if (CIVIC_NUMBER.test(place.address)) return place;
  const civic = CIVIC_NUMBER.exec(typed)?.[1];
  if (!civic) return place;
  return { ...place, address: `${civic} ${place.address.trim()}` };
}

/**
 * One row per visible line. A provider can answer with several features that
 * read identically — OSM splits a long street into many ways, so "Rue Weldon,
 * Moncton, NB" came back six times — and a list of identical rows only makes
 * the person wonder which is right. The first one wins; they resolve alike.
 */
export function dedupeSuggestions<S extends { primary: string; secondary?: string | null }>(
  list: S[],
): S[] {
  const seen = new Set<string>();
  return list.filter((s) => {
    const key = `${s.primary}\u0000${s.secondary ?? ''}`.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
